import { useSyncExternalStore } from 'react';
import { patchApi, type PatchView } from '../api/client';
import { busyInPatch, ringFor } from './calls';
import { myChoiceNow, useMessagesLive, type LiveEvent } from './messages';
import { inPatch, onPatchState, patchBusy, patchDeclined, patchIn, patchNow, patchPhones } from './patch';
// The chime while waiting for people to join a Patch you started (1.12.26).
import './patchWaiting';

/**
 * A Patch ringing this person (1.12.4).
 *
 * - Started in a direct message, a Patch rings the others like a call, on every device with Stitch
 *   open, for half a minute: Stitch's ring (call waiting's soft beep when they're on a call or in
 *   another Patch) and a card with Join and Decline. Their phone gets a notification too.
 * - It stops when they join or decline (on any of their devices), when the Patch ends, or after the
 *   half minute. A 1:1 nobody came to is "Missed Patch from Joey" in the conversation (the server
 *   posts it), and the person who started it sees "Sam can't join right now" when Sam says no.
 * - Someone on do not disturb isn't rung (the server leaves them out; this checks again here).
 * - Started in a channel, it only shows a quiet card for the same half minute: no sound, no
 *   notification.
 */
export interface PatchRing {
  /** "<tenant>|<patch id>". */
  key: string;
  tenant: string;
  conversationId: string;
  patchId: string;
  by: { id: string; name: string };
  /** For a channel, its name ("front-desk"); for a direct message, who's patching them in. */
  title: string;
  /** Started in a channel: the card shows, without a sound. */
  quiet: boolean;
  /** When it stops, on this device's clock. */
  until: number;
}

const RING_MS = 30_000;
const MAX_RINGS = 3;
let rings: PatchRing[] = [];
const listeners = new Set<() => void>();
const timers = new Map<string, number>();

function sync(next: PatchRing[]): void {
  const before = new Set(rings.filter((r) => !r.quiet).map((r) => r.key));
  const after = new Set(next.filter((r) => !r.quiet).map((r) => r.key));
  rings = next;
  for (const k of before) if (!after.has(k)) ringFor(`patch:${k}`, false);
  for (const k of after) if (!before.has(k)) ringFor(`patch:${k}`, true);
  for (const l of [...listeners]) l();
}

/** The Patches ringing now, and every change (headset buttons and lights, 3141). */
export const patchRingsNow = (): PatchRing[] => rings;
export function onPatchRings(l: () => void): () => void {
  listeners.add(l);
  return () => void listeners.delete(l);
}

export function usePatchRings(): PatchRing[] {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => rings,
    () => rings
  );
}

/** Stops one ringing (its card goes too, and its notification). */
export function stopRing(key: string): void {
  window.clearTimeout(timers.get(key));
  timers.delete(key);
  shown.get(key)?.close();
  shown.delete(key);
  if (rings.some((r) => r.key === key)) sync(rings.filter((r) => r.key !== key));
}

/**
 * Stitch is open but not in front: a notification says so too, with the same tag as the one the
 * phone gets, so a device with both shows one.
 */
const shown = new Map<string, Notification>();
function notifyRing(r: PatchRing): void {
  if (r.quiet || typeof Notification === 'undefined' || Notification.permission !== 'granted' || document.visibilityState === 'visible') return;
  try {
    const n = new Notification(`${r.by.name} patched you in`, { body: 'Join the Patch in Stitch', tag: `patch:${r.patchId}`, requireInteraction: true });
    n.onclick = () => {
      window.focus();
      n.close();
    };
    shown.set(r.key, n);
  } catch {
    // Some browsers only show notifications from the service worker: the ring is still heard.
  }
}

/** "Join": into the Patch (out of another one first; an app call is put on hold by the card). */
export async function joinRing(r: PatchRing): Promise<void> {
  stopRing(r.key);
  await patchIn({ tenant: r.tenant, conversationId: r.conversationId, title: r.quiet ? `#${r.title}` : r.title });
}

/** "Decline": it stops ringing here and on this person's other devices, and the others see they can't. */
export async function declineRing(r: PatchRing): Promise<void> {
  stopRing(r.key);
  await patchApi.decline(r.tenant, r.patchId).catch(() => undefined);
}

const isView = (x: unknown): x is PatchView =>
  Boolean(x) && typeof (x as PatchView).id === 'string' && typeof (x as PatchView).conversationId === 'string' && Array.isArray((x as PatchView).people);

/** Whether this event ends a ring: the Patch is over (or another one began), they're in it now (on any device), or they said no. */
export function endsRing(r: PatchRing, me: string, e: Pick<LiveEvent, 'conversationId' | 'patch' | 'declined'>): boolean {
  if (r.conversationId !== e.conversationId) return false;
  const view = isView(e.patch) ? e.patch : null;
  return !view || view.id !== r.patchId || view.people.some((p) => p.id === me) || e.declined?.id === me;
}

/**
 * The ring (or, from a channel, the quiet card) an event starts for this person, if any: not their
 * own Patch, not one they're in, not on do not disturb, and not one heard too late (Stitch catching
 * up after the connection dropped).
 */
export function ringOf(t: string, me: string, e: LiveEvent, ctx: { inIt: boolean; dnd: boolean; now: number }): PatchRing | null {
  const view = isView(e.patch) ? e.patch : null;
  const start = e.ring ?? e.started;
  if (!view || !start || !e.conversationId || start.by.id === me) return null;
  if (ctx.inIt || ctx.dnd || view.people.some((p) => p.id === me)) return null;
  const left = Math.min(RING_MS, Date.parse(start.until) - ctx.now);
  if (!(left > 1500)) return null;
  const patchId = e.patchId ?? view.id;
  return {
    key: `${t}|${patchId}`,
    tenant: t,
    conversationId: e.conversationId,
    patchId,
    by: start.by,
    title: e.started ? e.started.title : start.by.name,
    quiet: !e.ring,
    until: ctx.now + left
  };
}

/** A 'patch' event for this company: starts, stops or changes what rings. */
function heard(t: string, me: string, e: LiveEvent): void {
  for (const r of rings) if (r.tenant === t && endsRing(r, me, e)) stopRing(r.key);
  // Someone said no: the people in the Patch see it.
  const patchId = e.patchId ?? (isView(e.patch) ? e.patch.id : '');
  if (e.declined && e.declined.id !== me && e.conversationId) patchDeclined(t, e.conversationId, patchId, e.declined);
  // Desk phones rung for it, or on it (1.12.18).
  if (e.conversationId && isView(e.patch)) patchPhones(t, e.conversationId, e.patch);
  const ring = ringOf(t, me, e, { inIt: Boolean(e.conversationId && inPatch(patchNow(), t, e.conversationId)), dnd: myChoiceNow(t) === 'dnd', now: Date.now() });
  if (!ring || rings.some((r) => r.key === ring.key)) return;
  sync([ring, ...rings].slice(0, MAX_RINGS));
  notifyRing(ring);
  timers.set(
    ring.key,
    window.setTimeout(() => stopRing(ring.key), ring.until - Date.now())
  );
}

/** For the company's people while Stitch is open: a Patch ringing them, or starting in their channel. */
export function usePatchRingLive(tenant: string, myId: string | undefined, on: boolean): void {
  const t = tenant.toLowerCase();
  useMessagesLive(
    (e) => {
      if (e.type === 'reset') {
        for (const r of rings) if (r.tenant === t) stopRing(r.key);
        return;
      }
      if (e.type !== 'patch' || e.tenant?.toLowerCase() !== t || !myId) return;
      heard(t, myId, e);
    },
    on && Boolean(t) && Boolean(myId)
  );
}

if (typeof window !== 'undefined') {
  // In a Patch: something else ringing is call waiting's soft beep.
  onPatchState(() => busyInPatch(patchBusy()));
  // Signed out: nothing rings.
  window.addEventListener('stitch:signed-out', () => {
    for (const r of rings) stopRing(r.key);
  });
}
